import React, { useState } from 'react';
import { SuccessIcon, WarningIcon, GoalIcon, TipIcon } from '../../../icons';
import { DISCColor, DISCProfile, DISCScores } from './types';
import { DISC_QUESTIONS, DISC_PROFILE_DESCRIPTIONS } from './discQuestions';

interface DISCAssessmentProps {
  onComplete: (profile: DISCProfile) => void;
  onCancel: () => void;
}

/**
 * DISC Assessment Component
 * 
 * 15 vragen die je persoonlijkheidstype bepalen.
 * Gebruikt DISC model: Rood/Geel/Groen/Blauw
 */
const DISCAssessment: React.FC<DISCAssessmentProps> = ({
  onComplete,
  onCancel
}) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<DISCColor[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [profile, setProfile] = useState<DISCProfile | null>(null);

  const progress = ((currentQuestion + 1) / DISC_QUESTIONS.length) * 100;
  const question = DISC_QUESTIONS[currentQuestion];

  // Handle answer selection
  const handleAnswer = (color: DISCColor) => {
    const newAnswers = [...answers, color];
    setAnswers(newAnswers);

    if (currentQuestion < DISC_QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // All questions answered, calculate profile
      const calculatedProfile = calculateProfile(newAnswers);
      setProfile(calculatedProfile);
      setShowResults(true);
    }
  };

  // Go back to previous question
  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      setAnswers(prev => prev.slice(0, -1));
    }
  };

  // Calculate DISC profile from answers
  const calculateProfile = (answersList: DISCColor[]): DISCProfile => {
    // Count scores per color
    const scores: DISCScores = {
      red: 0,
      yellow: 0,
      green: 0,
      blue: 0
    };

    answersList.forEach(color => {
      scores[color] += 1;
    });

    // Determine primary and secondary colors
    const sortedColors = (Object.entries(scores) as [DISCColor, number][])
      .sort((a, b) => b[1] - a[1]);

    const primary = sortedColors[0][0];
    const secondary = sortedColors[1][0];

    // Get description for primary color
    const description = DISC_PROFILE_DESCRIPTIONS[primary];

    return {
      primary,
      secondary,
      scores,
      description: description.description,
      entrepreneurialStrengths: description.entrepreneurialStrengths,
      entrepreneurialWeaknesses: description.entrepreneurialWeaknesses,
      completedAt: Date.now()
    };
  };

  // Get color styling
  const getColorClass = (color: DISCColor) => {
    const classes = {
      red: 'bg-red-500 hover:bg-red-600 text-white',
      yellow: 'bg-yellow-400 hover:bg-yellow-500 text-gray-900',
      green: 'bg-green-500 hover:bg-green-600 text-white',
      blue: 'bg-blue-500 hover:bg-blue-600 text-white'
    };
    return classes[color];
  };

  const getColorBadgeClass = (color: DISCColor) => {
    const classes = {
      red: 'bg-red-100 text-red-700 border-red-300',
      yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      blue: 'bg-blue-100 text-blue-700 border-blue-300'
    };
    return classes[color];
  };

  const getColorLabel = (color: DISCColor) => {
    const labels = {
      red: '🔴 Rood (Doener)',
      yellow: '🟡 Geel (Netwerker)',
      green: '🟢 Groen (Teamspeler)',
      blue: '🔵 Blauw (Analist)'
    };
    return labels[color];
  };

  // Results view
  if (showResults && profile) {
    const primaryDesc = DISC_PROFILE_DESCRIPTIONS[profile.primary];
    const secondaryDesc = DISC_PROFILE_DESCRIPTIONS[profile.secondary];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90dvh] overflow-y-auto scrollbar-always">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-6 text-white">
            <h2 className="text-2xl font-bold mb-2">🎨 Jouw DISC Profiel</h2>
            <p className="text-purple-100">
              Ontdek hoe jouw persoonlijkheid je ondernemerschap beïnvloedt
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Primary Color */}
            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl border-4 ${getColorBadgeClass(profile.primary)}`}>
                  {getColorLabel(profile.primary).split(' ')[0]}
                </div>
                <div>
                  <p className="text-sm text-gray-600">Jouw Primaire Type</p>
                  <h3 className="text-2xl font-bold text-gray-900">{primaryDesc.title}</h3>
                </div>
              </div>
              <p className="text-gray-700 mb-4">{primaryDesc.description}</p>

              {/* Scores */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {(['red', 'yellow', 'green', 'blue'] as DISCColor[]).map(color => (
                  <div key={color} className="text-center">
                    <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center text-xl border-2 ${getColorBadgeClass(color)}`}>
                      {getColorLabel(color).split(' ')[0]}
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mt-2">{profile.scores[color]}</p>
                    <p className="text-xs text-gray-500">{getColorLabel(color).split(' ')[1]}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Secondary Color */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Secundair Type:</p>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getColorBadgeClass(profile.secondary)}`}>
                  {getColorLabel(profile.secondary)}
                </span>
                <span className="text-sm text-gray-600">({profile.scores[profile.secondary]} punten)</span>
              </div>
            </div>

            {/* Voor Ondernemerschap */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Strengths */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                  <SuccessIcon size={16} />
                  <span>Je Sterktes</span>
                </h4>
                <ul className="space-y-2">
                  {primaryDesc.entrepreneurialStrengths.map((strength, idx) => (
                    <li key={idx} className="text-sm text-green-800 flex items-start gap-2">
                      <span className="text-green-500 mt-0.5">•</span>
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weaknesses */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                  <WarningIcon size={16} />
                  <span>Let Op</span>
                </h4>
                <ul className="space-y-2">
                  {primaryDesc.entrepreneurialWeaknesses.map((weakness, idx) => (
                    <li key={idx} className="text-sm text-orange-800 flex items-start gap-2">
                      <span className="text-orange-500 mt-0.5">•</span>
                      <span>{weakness}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Ideal Roles */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-3"><GoalIcon size={16} className="inline" /> Ideaal voor jou:</h4>
              <div className="flex flex-wrap gap-2">
                {primaryDesc.idealRoles.map((role, idx) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    {role}
                  </span>
                ))}
              </div>
            </div>

            {/* Needs Support */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="font-semibold text-purple-900 mb-3"><TipIcon size={16} className="inline" /> Zoek ondersteuning bij:</h4>
              <div className="flex flex-wrap gap-2">
                {primaryDesc.needsSupport.map((need, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                    {need}
                  </span>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={onCancel}
                className="px-6 py-3 text-gray-600 hover:text-gray-800 font-medium"
              >
                ← Terug
              </button>
              <button
                onClick={() => onComplete(profile)}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-lg font-bold hover:from-purple-600 hover:to-pink-600 transition-all shadow-md hover:shadow-lg"
              >
                ✓ Profiel Opslaan & Verder →
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question view
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold">🎨 DISC Persoonlijkheidstest</h2>
            <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
              Vraag {currentQuestion + 1} / {DISC_QUESTIONS.length}
            </span>
          </div>
          {/* Progress bar */}
          <div className="w-full bg-purple-300/30 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="p-8">
          <div className="mb-6">
            <p className="text-lg text-gray-900 font-medium mb-1">{question.question}</p>
            <p className="text-sm text-gray-500">Kies het antwoord dat het beste bij je past</p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            {question.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option.color)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${getColorClass(option.color)} hover:scale-[1.02] hover:shadow-md`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{getColorLabel(option.color).split(' ')[0]}</span>
                  <span className="font-medium">{option.text}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={handleBack}
              disabled={currentQuestion === 0}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Vorige
            </button>
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-400 hover:text-gray-600 text-sm"
            >
              Annuleren
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DISCAssessment;
